import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PurchaseOrderSummary, PurchaseOrderStatus } from '../../../../types/procurement.types';
import { cn } from '../../../../components/ui/utils';
import { formatDate, money, orderReference, vendorName } from '../procurement-ui';
import {
    AmendmentChip,
    ClosedShortChip,
    PURCHASE_ORDER_OFF_FLOW_STATUSES,
    PURCHASE_ORDER_STATUS_LABELS,
    PURCHASE_ORDER_STATUS_ORDER,
    PurchaseOrderStatusPill,
    ReceivedProgress,
    receivedPercentLabel,
} from './PurchaseOrderStatus';

/**
 * Read-only Kanban of the PO register: one lane per status, no drag and drop.
 *
 * This is the same `orders` array the list view renders, only grouped — the two views can
 * never disagree about which order sits where, and the lane count falls back to the cards
 * actually on screen when `counts` has nothing for a status.
 */

interface PurchaseOrderKanbanProps {
    orders: PurchaseOrderSummary[];
    /** Server count per status across the current filters — the lane headers. */
    counts: Record<string, number>;
}

export default function PurchaseOrderKanban({ orders, counts }: PurchaseOrderKanbanProps) {
    const grouped = useMemo(() => {
        const byStatus: Partial<Record<PurchaseOrderStatus, PurchaseOrderSummary[]>> = {};
        orders.forEach((order) => {
            const bucket = byStatus[order.status];
            if (bucket) bucket.push(order);
            else byStatus[order.status] = [order];
        });
        return byStatus;
    }, [orders]);

    // The lifecycle always gets its lanes; rejected/cancelled only earn one when something
    // is in them (or the lane would be a permanent dead column).
    const lanes = [...PURCHASE_ORDER_STATUS_ORDER, ...PURCHASE_ORDER_OFF_FLOW_STATUSES]
        .filter((status) =>
            PURCHASE_ORDER_STATUS_ORDER.includes(status)
            || (counts[status] ?? grouped[status]?.length ?? 0) > 0);

    // One row at every width: lanes stay aligned and the strip scrolls sideways instead of
    // wrapping into ragged rows. Each lane keeps its own minimum width so cards never squish.
    return (
        <div className="flex w-full gap-3 overflow-x-auto overscroll-x-contain snap-x pb-2">
            {lanes.map((status) => {
                const cards = grouped[status] ?? [];
                const count = counts[status] ?? cards.length;

                return (
                    <section
                        key={status}
                        className="flex w-[280px] min-w-[280px] shrink-0 snap-start flex-col rounded-lg border border-border bg-muted/30 sm:w-[300px] sm:min-w-[300px] lg:w-[320px] lg:min-w-[320px]"
                    >
                        <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
                            <PurchaseOrderStatusPill status={status} />
                            <span className="text-xs font-semibold text-muted-foreground tabular-nums">
                                {count}
                            </span>
                        </div>

                        <div className="flex-1 space-y-2 p-2">
                            {cards.length === 0 ? (
                                <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                                    Nothing in {PURCHASE_ORDER_STATUS_LABELS[status].toLowerCase()}.
                                </p>
                            ) : cards.map((order) => (
                                <Link
                                    key={order.id}
                                    to={`/stock/procurement/purchase-orders/${order.id}`}
                                    className="block rounded-md border border-border bg-card p-3 shadow-sm transition-colors hover:border-slate-300 active:bg-accent/40"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <span className="font-mono text-sm font-medium">
                                            {orderReference(order)}
                                        </span>
                                        <span className="text-sm font-semibold tabular-nums">
                                            {money(order.totalValue)}
                                        </span>
                                    </div>
                                    <p className="mt-1 truncate text-xs text-muted-foreground">
                                        {vendorName(order.vendor)}
                                    </p>
                                    <p className="truncate text-xs text-muted-foreground">
                                        {order.branch.name}
                                        {order.project ? ` · ${order.project.name}` : ''}
                                    </p>

                                    <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                                        <span>Required {formatDate(order.requiredByDate)}</span>
                                        <span className="tabular-nums">
                                            {receivedPercentLabel(order.receivedPercent)}
                                        </span>
                                    </div>

                                    {(order.openAmendmentCount > 0 || order.closedShort) && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            <AmendmentChip count={order.openAmendmentCount} />
                                            {order.closedShort && <ClosedShortChip />}
                                        </div>
                                    )}
                                </Link>
                            ))}
                        </div>
                    </section>
                );
            })}
        </div>
    );
}
